/**
 * Eine Prüfung für einen Spielplatz einplanen.
 *
 * Der Anlagetunnel erledigt das für neue Plätze mit. Diesen Weg braucht es
 * für alles, was vorher da war: ein Platz, der noch nie geprüft wurde,
 * hatte bisher keine Möglichkeit, unter „Fällig“ aufzutauchen — eine
 * Aufgabe entstand nur beim Abschluss einer Prüfung, und die erste
 * Prüfung entstand nur, wenn jemand von selbst daran dachte.
 */

import { useState } from 'react'
import { useAufgabeAnlegen } from '../api/aufgaben'
import { Blatt } from './Blatt'
import { Fehlerhinweis } from './Hinweise'

/* Regelabstände wie im Server. Sie stehen hier, damit die Oberfläche
   anzeigen kann, wann es das nächste Mal fällig wird. */
const INTERVALL: Record<string, number | null> = {
  hauptuntersuchung: 12,
  operativ: 3,
  abnahme: null,
  nachpruefung: null,
}

const ARTEN: { wert: string; text: string; erklaerung: string }[] = [
  {
    wert: 'hauptuntersuchung',
    text: 'Jährliche Hauptuntersuchung',
    erklaerung: 'Die große Prüfung nach DIN EN 1176-7, einmal im Jahr.',
  },
  {
    wert: 'operativ',
    text: 'Operative Kontrolle',
    erklaerung: 'Funktion und Stabilität, üblicherweise vierteljährlich.',
  },
  {
    wert: 'abnahme',
    text: 'Abnahmeprüfung',
    erklaerung: 'Einmalig nach Neubau oder Umbau. Wiederholt sich nicht.',
  },
]

export function Einplanen({
  spielplatzId,
  spielplatzName,
  schliessen,
}: {
  spielplatzId: string
  spielplatzName: string
  schliessen: () => void
}) {
  const anlegen = useAufgabeAnlegen()
  const [art, setzeArt] = useState('hauptuntersuchung')
  const [faellig, setzeFaellig] = useState(() => new Date().toISOString().slice(0, 10))

  const gewaehlt = ARTEN.find((a) => a.wert === art)

  return (
    <Blatt titel="Prüfung einplanen" untertitel={spielplatzName} schliessen={schliessen}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          anlegen.mutate(
            {
              spielplatz_id: spielplatzId,
              pruefart: art,
              faellig_am: faellig,
              intervall_monate: INTERVALL[art],
            },
            { onSuccess: schliessen },
          )
        }}
      >
        <fieldset className="space-y-2">
          <legend className="mb-1 block font-semibold text-slate-800">Prüfart</legend>
          {ARTEN.map((a) => (
            <label
              key={a.wert}
              className={`flex cursor-pointer items-start gap-3 rounded-feld border-2 p-3 font-normal ${
                art === a.wert ? 'border-akzent-500 bg-akzent-grund' : 'border-slate-200'
              }`}
            >
              <input
                type="radio"
                name="pruefart"
                value={a.wert}
                checked={art === a.wert}
                onChange={() => setzeArt(a.wert)}
                className="mt-1 h-6 w-6 shrink-0"
                style={{ minHeight: 'auto' }}
              />
              <span>
                <span className="font-semibold">{a.text}</span>
                <span className="block text-base text-slate-700">{a.erklaerung}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <div>
          <label htmlFor="faellig">Fällig am</label>
          <input
            id="faellig"
            type="date"
            value={faellig}
            onChange={(e) => setzeFaellig(e.target.value)}
            required
          />
          <p className="mt-1 text-base text-slate-600">
            28 Tage vorher geht eine Erinnerung an den zuständigen Prüfer.
            {INTERVALL[art]
              ? ` Nach dem Abschluss entsteht die nächste Aufgabe automatisch, ${
                  INTERVALL[art] === 12 ? 'ein Jahr' : `${INTERVALL[art]} Monate`
                } später.`
              : ' Diese Prüfart wiederholt sich nicht von selbst.'}
          </p>
        </div>

        <Fehlerhinweis fehler={anlegen.error} />

        <div className="flex flex-wrap gap-2">
          <button type="submit" className="knopf-haupt" disabled={anlegen.isPending}>
            {anlegen.isPending ? 'Plant ein …' : `${gewaehlt?.text} einplanen`}
          </button>
          <button type="button" className="knopf-still" onClick={schliessen}>
            Abbrechen
          </button>
        </div>
      </form>
    </Blatt>
  )
}
